import { LitElement, html, PropertyValues } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import ScrollToTargetController from '../../../ui/reactiveControllers/ScrollToTargetController';
import { TargetType } from '../types';
import { NavButtonEvent, NavButtonType } from '../../networkAppContainer/gzNavigationButton/types';

/**
 * @summary This component is a wrapper for the elements in the Network v2 side panel. It provides a scrollable area.
 * @tag gz-network-scrollable-wrapper
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-network-scrollable-wrapper')
export class NetworkScrollableWrapperWebComponent extends LitElement {
  private scrollToTargetController: ScrollToTargetController;
  private intersectionObserver: IntersectionObserver;

  @property({ type: Object })
  topTargetSelector: {
    selector: string;
    label: string;
  };

  @property({ type: Object })
  bottomTargetSelector: {
    selector: string;
    label: string;
  };

  @query('slot')
  private slotElement: HTMLSlotElement;

  private topTarget: Element | null = null;
  private bottomTarget: Element | null = null;

  private get slottedElements(): HTMLElement[] {
    return this.slotElement.assignedElements() as HTMLElement[];
  }

  static get styles() {
    return styles;
  }

  constructor() {
    super();
    this.intersectionObserver = new IntersectionObserver(this.onViewIntersection.bind(this), {
      threshold: [0],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.intersectionObserver.disconnect();
    if (this.slotElement) {
      this.slotElement.removeEventListener('treeLoaded', this.setupObservers.bind(this));
    }
  }

  protected async firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    await this.updateComplete;
    this.scrollToTargetController = new ScrollToTargetController(this, this);

    if (this.slotElement) {
      this.slotElement.addEventListener('treeLoaded', this.setupObservers.bind(this));
    }
  }

  private setupObservers(): void {
    this.topTarget = this.slottedElements.find(el => el.id === this.topTargetSelector.selector) as HTMLElement;
    this.bottomTarget = this.slottedElements.find(el => el.id === this.bottomTargetSelector.selector) as HTMLElement;

    // Disconnect any previous observers first
    this.intersectionObserver.disconnect();

    if (this.topTarget) {
      this.intersectionObserver.observe(this.topTarget);
    }

    if (this.bottomTarget) {
      this.intersectionObserver.observe(this.bottomTarget);
    }
  }

  private onViewIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach((entry: IntersectionObserverEntry) => {
      let buttonType: NavButtonType | undefined = undefined;

      if (entry.target.id === this.topTargetSelector.selector) {
        buttonType = NavButtonType.TOP;
      }

      if (entry.target.id === this.bottomTargetSelector.selector) {
        buttonType = NavButtonType.BOTTOM;
      }

      if (buttonType) {
        this.dispatchEvent(
          new CustomEvent(NavButtonEvent.NAV_BUTTON_DISPLAY_CHANGE, {
            detail: {
              buttonType: buttonType,
              isVisible: !entry.isIntersecting,
            },
            bubbles: true,
            composed: true,
          })
        );
      }
    });
  }

  private scrollToTarget(targetSelector: string): void {
    const target = this.slottedElements.find(el => el.id === targetSelector);
    if (target) {
      this.scrollToTargetController.scrollToTarget(target);
      setTimeout(() => {
        this.setupObservers();
        let buttonType: NavButtonType | undefined = undefined;

        // Manually set button states based on visibility
        if (targetSelector === this.topTargetSelector?.selector) {
          buttonType = NavButtonType.TOP;
        } else if (targetSelector === this.bottomTargetSelector?.selector) {
          buttonType = NavButtonType.BOTTOM;
        }
        if (buttonType) {
          this.dispatchEvent(
            new CustomEvent(NavButtonEvent.NAV_BUTTON_DISPLAY_CHANGE, {
              detail: {
                buttonType: buttonType,
                isVisible: false,
              },
              bubbles: true,
              composed: true,
            })
          );
        }
      }, 10);
    }
  }

  public handleScrollToTarget(targetType: TargetType): void {
    if (targetType === TargetType.TOP && this.topTargetSelector && this.topTargetSelector.selector) {
      this.scrollToTarget(this.topTargetSelector.selector);
    }

    if (targetType === TargetType.BOTTOM && this.bottomTargetSelector && this.bottomTargetSelector.selector) {
      this.scrollToTarget(this.bottomTargetSelector.selector);
    }
  }

  render() {
    return html` <slot></slot> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-network-scrollable-wrapper': NetworkScrollableWrapperWebComponent;
  }
}
